import React, { useState, useEffect, useContext, useCallback } from "react";
import { Button, Input, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../Header/Header";
import { AuthContext } from "../../contexts/AuthContext";
import { SECCIONES_PERMISOS, CLAVES_PERMISOS, PLANTILLAS, ALCANCES } from "./permisos";

const auth = () => ({ headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });
const error = (e) => Swal.fire({ icon: "error", title: "No se pudo guardar", text: (e.response && e.response.data && e.response.data.message) || "Intenta de nuevo" });

// Usuarios: se crean desde Empleados; aquí se asignan tienda, alcance y permisos.
function ListadoUsuarios() {
  const { user } = useContext(AuthContext);
  const URL_USERS = process.env.REACT_APP_URL_USERS;
  const URL_AREAS = process.env.REACT_APP_URL_AREAS;
  const puedeEditar = !!user.usuarios_permisos;

  const [usuarios, setUsuarios] = useState([]);
  const [areas, setAreas] = useState([]);
  const [estado, setEstado] = useState("Si");
  const [texto, setTexto] = useState("");
  const [filtroArea, setFiltroArea] = useState("");
  const [form, setForm] = useState(null); // { _id, nombre, area, rolTiendas, permisos:{} }
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(() => {
    axios.get(`${URL_USERS}Status/${estado}`, auth())
      .then((r) => setUsuarios(r.data.filter((u) => !u.menu_edoCta_Cliente).sort((a, b) => (a.nombre > b.nombre ? 1 : -1))))
      .catch(() => setUsuarios([]));
  }, [URL_USERS, estado]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { axios.get(URL_AREAS, auth()).then((r) => setAreas(r.data)).catch(() => {}); }, [URL_AREAS]);

  const areaDe = (u) => (u.areas && u.areas[0] ? u.areas[0] : null);
  const esTienda = (idArea) => !!areas.find((a) => a._id === idArea && a.tienda);
  const cuenta = (u) => CLAVES_PERMISOS.filter((k) => u[k]).length;

  const abrir = (u) => {
    const permisos = {};
    CLAVES_PERMISOS.forEach((k) => { permisos[k] = !!u[k]; });
    setForm({ _id: u._id, nombre: `${u.nombre} ${u.apellido}`, area: areaDe(u) ? areaDe(u)._id : "", rolTiendas: u.rolTiendas || "", permisos });
  };

  const aplicaPlantilla = (p) => {
    const permisos = {};
    CLAVES_PERMISOS.forEach((k) => { permisos[k] = p.claves ? p.claves.includes(k) : true; });
    let rol = p.alcance;
    if (rol === "tienda" && !esTienda(form.area)) rol = "master";
    setForm({ ...form, rolTiendas: rol, permisos });
  };

  const cambiaArea = (idArea) => {
    // Quien trabaja en tienda queda, por omisión, limitado a su tienda
    const rol = esTienda(idArea) ? (form.rolTiendas === "master" ? "master" : "tienda") : (form.rolTiendas === "tienda" ? "" : form.rolTiendas);
    setForm({ ...form, area: idArea, rolTiendas: rol });
  };

  const soloTienda = form && form.rolTiendas === "tienda";
  const sinTiendas = form && form.rolTiendas === "";

  const guardar = async () => {
    if (form.rolTiendas === "tienda" && !esTienda(form.area)) return Swal.fire({ icon: "warning", title: "Elige su tienda", text: "Para limitarlo a su tienda, primero indica en qué tienda trabaja." });
    const datos = { areas: form.area, rolTiendas: form.rolTiendas };
    SECCIONES_PERMISOS.forEach((s) => s.items.forEach((i) => {
      // Usuario de tienda: solo permisos de tienda (el servidor le bloquea lo demás);
      // sin acceso a tiendas: se apagan los permisos de tienda
      let v = !!form.permisos[i.k];
      if (soloTienda && !s.tienda) v = false;
      if (sinTiendas && s.tienda) v = false;
      datos[i.k] = v;
    }));
    setGuardando(true);
    try {
      await axios.patch(`${URL_USERS}/${form._id}`, datos, auth());
      Swal.fire({ icon: "success", title: "Permisos guardados", text: "Ya aplican; los botones de su pantalla se actualizan cuando vuelva a entrar.", timer: 2200, showConfirmButton: false });
      setForm(null);
      cargar();
    } catch (e) { error(e); } finally { setGuardando(false); }
  };

  const alternarActivo = async (u) => {
    const activar = u.is_active !== "Si";
    const r = await Swal.fire({ title: `${activar ? "¿Activar" : "¿Desactivar"} a ${u.nombre}?`, text: activar ? "Podrá volver a entrar al sistema." : "Ya no podrá entrar al sistema.", icon: "question", showCancelButton: true, confirmButtonText: "Sí", cancelButtonText: "No" });
    if (!r.value) return;
    try { await axios.patch(`${URL_USERS}/${u._id}`, { is_active: activar ? "Si" : "No" }, auth()); cargar(); } catch (e) { error(e); }
  };

  const restablecer = async (u) => {
    const nueva = Math.random().toString(36).slice(2, 8);
    const r = await Swal.fire({ title: `¿Nueva contraseña para ${u.nombre}?`, html: `Se le asignará: <b>${nueva}</b>`, icon: "question", showCancelButton: true, confirmButtonText: "Sí, cambiar", cancelButtonText: "No" });
    if (!r.value) return;
    try {
      await axios.patch(`${URL_USERS}/passwordCliente/${u._id}`, { password: nueva }, auth());
      Swal.fire({ icon: "success", title: "Contraseña cambiada", html: `Usuario: <b>${u.email}</b><br/>Contraseña: <b>${nueva}</b>` });
    } catch (e) { error(e); }
  };

  const vis = usuarios.filter((u) =>
    `${u.nombre} ${u.apellido} ${u.email}`.toLowerCase().includes(texto.toLowerCase()) &&
    (!filtroArea || (areaDe(u) && areaDe(u)._id === filtroArea)));

  if (!user.menu_usuarios) {
    return (<><Header /><div className="card container col-12 p-4">No tienes permiso para ver usuarios.</div></>);
  }

  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "18px 22px" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title"><i className="fas fa-user-lock mr-2" style={{ color: "#6366f1" }} />Usuarios y permisos</h3>
            <p className="page-subtitle">Los usuarios se crean al dar de alta un empleado; aquí se asigna su tienda, alcance y permisos</p>
          </div>
          <div className="page-actions">
            {user.empleados_create && <Button color="success" href="/EmpleadosCreate"><i className="fas fa-user-plus mr-1" />Alta de empleado</Button>}
          </div>
        </div>

        <div className="filter-bar">
          <div style={{ flex: 1, minWidth: 200 }}><label>Buscar</label><Input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Nombre o correo" /></div>
          <div style={{ minWidth: 200 }}><label>Trabaja en</label>
            <Input type="select" value={filtroArea} onChange={(e) => setFiltroArea(e.target.value)}>
              <option value="">Todos</option>
              {areas.slice().sort((a, b) => (a.name > b.name ? 1 : -1)).map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
            </Input>
          </div>
          <div><label>Estado</label>
            <Input type="select" value={estado} onChange={(e) => setEstado(e.target.value)}>
              <option value="Si">Activos</option><option value="No">Inactivos</option>
            </Input>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-sm table-dark-header">
            <thead><tr><th>Nombre</th><th>Usuario</th><th>Trabaja en</th><th>Tiendas</th><th className="text-right">Permisos</th><th style={{ width: 150 }} /></tr></thead>
            <tbody>
              {!vis.length && <tr><td colSpan={6} className="text-center text-muted py-4">Sin usuarios</td></tr>}
              {vis.map((u) => (
                <tr key={u._id}>
                  <td><strong>{u.nombre} {u.apellido}</strong></td>
                  <td>{u.email}</td>
                  <td>{areaDe(u) ? areaDe(u).name : "—"}</td>
                  <td>
                    {u.rolTiendas === "master" ? <span className="badge badge-primary">Todas</span>
                      : u.rolTiendas === "tienda" ? <span className="badge badge-info">Su tienda</span>
                      : <span className="badge badge-secondary">Sin acceso</span>}
                  </td>
                  <td className="text-right">{cuenta(u)} de {CLAVES_PERMISOS.length}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {puedeEditar && <Button size="sm" color="success" onClick={() => abrir(u)} title="Permisos"><i className="fas fa-key" /></Button>}{" "}
                    {(puedeEditar || user.empleados_create) && <Button size="sm" color="info" onClick={() => restablecer(u)} title="Nueva contraseña"><i className="fas fa-unlock-alt" /></Button>}{" "}
                    {(puedeEditar || user.empleados_create) && u._id !== user.id && (
                      <Button size="sm" color={u.is_active === "Si" ? "danger" : "secondary"} onClick={() => alternarActivo(u)} title={u.is_active === "Si" ? "Desactivar" : "Activar"}>
                        <i className={`fas fa-${u.is_active === "Si" ? "user-slash" : "user-check"}`} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={!!form} toggle={() => setForm(null)} size="xl">
        <ModalHeader toggle={() => setForm(null)}>Permisos de {form && form.nombre}</ModalHeader>
        {form && (
          <ModalBody>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label>Trabaja en</label>
                <Input type="select" value={form.area} onChange={(e) => cambiaArea(e.target.value)}>
                  <optgroup label="Oficina">
                    {areas.filter((a) => !a.tienda).map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
                  </optgroup>
                  <optgroup label="Tiendas">
                    {areas.filter((a) => a.tienda && a.is_active !== "No").sort((a, b) => (a.name > b.name ? 1 : -1)).map((a) => <option key={a._id} value={a._id}>{a.clave ? `${a.clave} · ` : ""}{a.name}</option>)}
                  </optgroup>
                </Input>
              </div>
              <div className="col-md-4 mb-3">
                <label>Acceso a tiendas</label>
                <Input type="select" value={form.rolTiendas} onChange={(e) => setForm({ ...form, rolTiendas: e.target.value })}>
                  {Object.entries(ALCANCES).map(([k, l]) => <option key={k} value={k} disabled={k === "tienda" && !esTienda(form.area)}>{l}</option>)}
                </Input>
              </div>
              <div className="col-md-4 mb-3">
                <label>Plantilla rápida</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {PLANTILLAS.map((p) => <Button key={p.nombre} size="sm" color="light" style={{ border: "1px solid #e2e8f0" }} onClick={() => aplicaPlantilla(p)}>{p.nombre}</Button>)}
                </div>
              </div>
            </div>
            {soloTienda && <div className="alert alert-info py-2">Usuario de tienda: solo entra al Punto de Venta de su tienda. Los permisos de Modelos, Empleados y Usuarios no aplican.</div>}
            {sinTiendas && <div className="alert alert-secondary py-2">Sin acceso a tiendas: no verá el Punto de Venta.</div>}

            <div className="row">
              {SECCIONES_PERMISOS.map((s) => {
                const apagada = (soloTienda && !s.tienda) || (sinTiendas && s.tienda);
                const todas = s.items.every((i) => form.permisos[i.k]);
                return (
                  <div key={s.seccion} className="col-md-6 col-xl-4 mb-3">
                    <div className="form-section" style={{ height: "100%", opacity: apagada ? 0.45 : 1 }}>
                      <div className="form-section-header">
                        <i className={s.icono} />
                        <h4 className="form-section-title">{s.seccion}</h4>
                        {!apagada && (
                          <Button size="sm" color="link" className="ml-auto p-0" onClick={() => {
                            const permisos = { ...form.permisos };
                            s.items.forEach((i) => { permisos[i.k] = !todas; });
                            setForm({ ...form, permisos });
                          }}>{todas ? "Quitar todo" : "Marcar todo"}</Button>
                        )}
                      </div>
                      <div className="form-section-body" style={{ padding: "10px 16px" }}>
                        {s.items.map((i) => (
                          <label key={i.k} style={{ display: "flex", gap: 8, alignItems: "flex-start", margin: "0 0 6px", fontWeight: 400, textTransform: "none", letterSpacing: 0, cursor: apagada ? "not-allowed" : "pointer" }}>
                            <input type="checkbox" style={{ marginTop: 4 }} disabled={apagada} checked={!apagada && !!form.permisos[i.k]}
                              onChange={(e) => setForm({ ...form, permisos: { ...form.permisos, [i.k]: e.target.checked } })} />
                            <span>
                              <span style={{ fontSize: "0.85rem", color: "#0f172a" }}>{i.label}</span>
                              {i.desc && <span style={{ display: "block", fontSize: "0.72rem", color: "#64748b" }}>{i.desc}</span>}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ModalBody>
        )}
        <ModalFooter>
          <Button color="secondary" onClick={() => setForm(null)}>Cancelar</Button>
          <Button color="success" disabled={guardando} onClick={guardar}>{guardando ? <i className="fas fa-spinner fa-spin" /> : "Guardar permisos"}</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default ListadoUsuarios;
